import React from "react";
import { DropdownInput, IOption } from "component-library";

import { IWeightViewModel } from "../../../../models/views";
import { MeasurementSystems } from "../../../../models";

export enum WeightUnits {
  stone = "stone",
  pound = "pound",
  kilogram = "kilogram",
}

export interface IWeightContent {
  stoneField: string;
  stonePlaceholder: string;
  poundField: string;
  poundPlaceholder: string;
  kilogramField: string;
  kilogramPlaceholder: string;
}

interface IWeightProps {
  model: IWeightViewModel;
  measurementSystem: MeasurementSystems;
  content: IWeightContent;
  handleChanges: (model: IWeightViewModel) => void;
}

export default class Weight extends React.Component<IWeightProps> {
  public render() {
    const { content, measurementSystem } = this.props;

    const isImperial = measurementSystem === MeasurementSystems.imperial;

    return (
      <div className="c-additional-info-form__control-grid-2">
        {isImperial && (
          <div className="c-additional-info-form__control-grid-cell">
            {this.renderDropDown(WeightUnits.stone, {
              label: content.stoneField,
              placeholder: content.stonePlaceholder,
            })}
          </div>
        )}
        <div className="c-additional-info-form__control-grid-cell">
          {isImperial
            ? this.renderDropDown(WeightUnits.pound, {
                label: content.poundField,
                placeholder: content.poundPlaceholder,
              })
            : this.renderDropDown(WeightUnits.kilogram, {
                label: content.kilogramField,
                placeholder: content.kilogramPlaceholder,
              })}
        </div>
      </div>
    );
  }

  private renderDropDown = (
    unit: WeightUnits,
    text: { label: string; placeholder: string }
  ) => {
    let options: IOption<number>[] = [
      this.getEmptyOption(text.placeholder),
      ...this.getOptions(unit),
    ];

    const weight = this.props.model.weight;
    const { stones = 0, pounds = 0 } = this.kilogramsToImperial(weight);

    let value: number | undefined = undefined;

    if (weight) {
      switch (unit) {
        case WeightUnits.stone:
          value = Math.round(stones);
          break;
        case WeightUnits.pound:
          value = Math.round(pounds);
          break;
        case WeightUnits.kilogram:
          value = Math.round(weight);
          break;
      }
    }

    return (
      <DropdownInput<number>
        key={unit + this.props.measurementSystem.toString()}
        id={`weight${unit}`}
        variant="select"
        options={options}
        name={unit}
        value={value}
        {...text}
        valueChanged={this.dropdownChanged(unit)}
      />
    );
  };

  private dropdownChanged = (unit: WeightUnits) => {
    return (value: number) => {
      const updatedModel = { ...this.props.model };
      if (unit !== WeightUnits.kilogram) {
        let { pounds, stones } = this.kilogramsToImperial(updatedModel.weight);

        if (unit === WeightUnits.stone) {
          stones = value || 0;
        } else {
          pounds = value || 0;
        }

        updatedModel.weight =
          Math.round((stones * 14 + pounds) / 0.2204623) / 10;
      } else {
        updatedModel.weight = value;
      }

      if (
        updatedModel.weight !== this.props.model.weight ||
        Number.isNaN(value)
      ) {
        this.props.handleChanges(updatedModel);
      }
    };
  };

  private getOptions(unit: WeightUnits) {
    let options: IOption<number>[] = [];

    switch (unit) {
      case WeightUnits.stone:
        options = this.getNumberList(2, 43);
        break;
      case WeightUnits.pound:
        options = this.getNumberList(0, 13);
        break;
      case WeightUnits.kilogram:
        options = this.getNumberList(13, 279);
        break;
    }

    return options;
  }

  private getEmptyOption = (placeholder: string): IOption<number> => ({
    value: NaN,
    name: placeholder,
  });

  private getNumberList = (from: number, to: number) => {
    const options: IOption<number>[] = [];
    for (let weight = from; weight <= to; weight++) {
      options.push({ name: weight.toString(), value: weight });
    }

    return options;
  };

  private kilogramsToImperial = (kilograms?: number) => {
    let pounds = 0;
    let stones = 0;

    if (kilograms) {
      let realPounds = Math.round(kilograms * 2.204623);
      pounds = realPounds % 14;
      stones = (realPounds - pounds) / 14;
    }
    return { pounds, stones };
  };
}
